<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import Card from '@uiKit/components/Card.vue'
import SearchInput from '@uiKit/components/SearchInput.vue'
import TablePagination from '@uiKit/components/TablePagination.vue'

// The card every paged list in the portal sits in: a toolbar (title, total and
// search, plus an optional actions slot), a column header, then exactly one body
// — skeleton, error, the caller's empty state, or the rows — and the pager.
//
// Rows are the caller's `<li>` elements in the default slot. They lay out on the
// same grid as the header by reading `var(--list-table-columns)`, which this card
// sets from `gridTemplate`; below 992px the header hides and rows are expected to
// regroup themselves into cards.
export type ListTableState = 'loading' | 'error' | 'empty' | 'ready'

export interface ListTableColumn {
  key: string
  label: string
}

export interface ListTablePagination {
  page: number
  totalPages: number
  totalItems: number
  pageSize: number
}

const searchTerm = defineModel<string>('search', { required: true })

const props = withDefaults(
  defineProps<{
    title: string
    state: ListTableState
    columns: readonly ListTableColumn[]
    /** CSS grid-template-columns shared by the header and every row. */
    gridTemplate: string
    /** Null until the first page loads; kept by the caller through a failed fetch. */
    pagination: ListTablePagination | null
    itemLabel: string
    itemsLabel: string
    searchPlaceholder: string
    errorMessage: string
    retryLabel?: string
    isSearchDisabled?: boolean
    /** The previous page stays on screen, dimmed, while the next one loads. */
    isRefreshing?: boolean
    skeletonRows?: number
  }>(),
  { retryLabel: 'Try again', isSearchDisabled: false, isRefreshing: false, skeletonRows: 5 },
)

const emit = defineEmits<{ 'update:page': [page: number]; retry: [] }>()

const totalLabel = computed(() => {
  const total = props.pagination?.totalItems
  if (total === undefined) return ''
  return `${total} ${total === 1 ? props.itemLabel : props.itemsLabel}`
})

// Shown whenever there is something to page through — including over an empty
// state that only filters the page on screen, so the reader can try another page.
const showPagination = computed(
  () => props.state !== 'loading' && (props.pagination?.totalItems ?? 0) > 0,
)
</script>

<template>
  <Card
    variant="outlined"
    padding="0"
    class="list-table"
    :style="{ '--list-table-columns': gridTemplate }"
    :aria-busy="state === 'loading' || isRefreshing"
  >
    <Box flex align="center" justify="between" gap="3" class="list-table__toolbar">
      <Box direction="col" gap="1">
        <Text is="h2" type="heading" weight="semibold">{{ title }}</Text>
        <Text v-if="totalLabel" type="caption">{{ totalLabel }}</Text>
      </Box>
      <Box flex align="center" gap="3" class="list-table__actions">
        <Box class="list-table__search">
          <SearchInput
            v-model="searchTerm"
            :placeholder="searchPlaceholder"
            :disabled="isSearchDisabled"
          />
        </Box>
        <slot name="actions" />
      </Box>
    </Box>

    <Box class="list-table__head" aria-hidden="true">
      <Text
        v-for="column in columns"
        :key="column.key"
        is="span"
        type="caption-sm"
        weight="semibold"
        transform="uppercase"
        class="list-table__head-cell"
      >
        {{ column.label }}
      </Text>
      <Box is="span" />
    </Box>

    <Box v-if="state === 'loading'" direction="col" class="list-table__skeleton">
      <Box v-for="index in skeletonRows" :key="index" flex align="center" gap="3" padding="4">
        <Skeleton size="40px" border-radius="10px" />
        <Box direction="col" gap="2" grow>
          <Skeleton width="40%" height="0.875rem" />
          <Skeleton width="25%" height="0.75rem" />
        </Box>
      </Box>
    </Box>

    <Box v-else-if="state === 'error'" padding="4">
      <Message color="danger" variant="outlined">
        <Box flex align="center" justify="between" gap="3" class="list-table__error">
          <Text is="span" type="body-sm" color="inherit">{{ errorMessage }}</Text>
          <Button
            :label="retryLabel"
            variant="outline"
            color="danger"
            size="small"
            @click="emit('retry')"
          />
        </Box>
      </Message>
    </Box>

    <Box v-else-if="state === 'empty'" padding="6">
      <slot name="empty" />
    </Box>

    <Box
      v-else
      is="ul"
      role="list"
      class="list-table__rows"
      :class="{ 'list-table__rows--refreshing': isRefreshing }"
    >
      <slot />
    </Box>

    <TablePagination
      v-if="pagination && showPagination"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total-items="pagination.totalItems"
      :page-size="pagination.pageSize"
      :item-label="itemLabel"
      :items-label="itemsLabel"
      @change="emit('update:page', $event)"
    />
  </Card>
</template>

<style scoped>
.list-table {
  overflow: hidden;
}

.list-table__toolbar {
  flex-wrap: wrap;
  padding: 0.875rem 1.25rem;
}

.list-table__actions {
  flex-wrap: nowrap;
  justify-content: flex-end;
}

/* A fixed width, not a percentage: the actions row sizes to its content, so a
   percentage resolved against it and stretched the search over the action
   buttons, pushing them onto a second line. */
.list-table__search {
  width: 20rem;
  max-width: 100%;
}

/* Slotted action buttons keep their label on one line beside the search. */
.list-table__actions > :slotted(*) {
  flex-shrink: 0;
  white-space: nowrap;
}

.list-table__head {
  display: grid;
  grid-template-columns: var(--list-table-columns);
  column-gap: 1rem;
  align-items: center;
  padding: 0.625rem 1.25rem;
  background: var(--p-surface-50);
  border-top: 1px solid var(--p-content-border-color);
  border-bottom: 1px solid var(--p-content-border-color);
}

.list-table__head-cell {
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.list-table__skeleton > * + * {
  border-top: 1px solid var(--p-content-border-color);
}

.list-table__error {
  flex-wrap: wrap;
  width: 100%;
}

.list-table__rows {
  list-style: none;
  margin: 0;
  padding: 0;
  transition: opacity 0.15s ease;
}

.list-table__rows--refreshing {
  opacity: 0.55;
}

@media (max-width: 992px) {
  /* Rows become self-labelled cards, so the column header goes. */
  .list-table__head {
    display: none;
  }

  .list-table__toolbar {
    align-items: stretch;
    flex-direction: column;
    padding: 1rem;
  }

  .list-table__actions {
    width: 100%;
    justify-content: stretch;
  }

  .list-table__search {
    flex: 1 1 220px;
    width: auto;
  }
}

/* Only a phone too narrow for both puts the actions under the search. */
@media (max-width: 480px) {
  .list-table__actions {
    flex-wrap: wrap;
  }
}
</style>
